// The chrome theme (R243, corrected by R255).
//
// The DOM chrome carries one palette, and until R243 that palette was the
// Abyssal Compact's cyan, hard-coded. `--accent: #35e0d2` is faction 1's
// colour exactly, so a player in the Void Choir played a cyan game.
//
// **What is themed:** the DOM chrome only — `ui/styles.css` and
// `ui/hub.css`. The canvas is not themed at all. The five faction colours
// on the map are the map's legend, and a legend that moved with a setting
// would be a lie.
//
// **The rule, not five hand-picks.** Every token below is today's value
// with its hue turned by one angle: the difference between the faction's
// own hue and the Abyssal Compact's. Lightness and chroma are held, so the
// chrome keeps the ink family it was drawn with and only changes banner.
// Faction 1 turns by zero degrees, so it must come out byte-identical to
// the values the stylesheet shipped with — that is the check that the
// derivation is right, and `theme.test.ts` pins it.
//
// **The turn is taken in OKLCh and not in HSL (R255).** R243 turned the
// hue in HSL and held saturation and lightness, and the grounds it built
// were too intense. HSL saturation is not a perceptual quantity: the same
// number buys much more colour in red and violet than it does in blue.
// Measured in OKLab against the Abyssal Compact's own panel ink, L 0.185
// and C 0.032, the HSL turn handed the Void Choir C 0.059 (+85%) and the
// Rust Fleet L 0.233 (+26%). OKLab carries the two axes an eye reads, so
// a turn taken in it holds both for real: every faction now gets the
// ground the chrome was drawn with, and only the hue moves.
//
// `--accent` is the exception: it is set to the faction's colour itself,
// not to a turn of the cyan, because the accent is the one token that
// must match the banner a player sees on the map.
//
// **What this file must never write:** a faction colour, a resource hue,
// `--bad`, `--good` or `--gold`. A refusal, a confirmation and a price
// look the same under every banner.

import { FACTIONS, factionOf } from '../world';
import { channels, packHex } from '../render/color';
// The panel alpha and the contrast nudge ride presentation.toml
// [presentation.chrome]. TEXT_CONTRAST_FLOOR stays in code: it is
// WCAG AA and not a look the owner tunes.
import { CHROME_EDGE_ALPHA, CHROME_NUDGE_STEP, CHROME_PANEL_ALPHA } from '../gen/presentation';
import { LIGHT_CHROME_RULE } from '../gen/inks';
import { LIGHT } from '../identity/pick';
import { writeStore } from './localstore';
import { chromeGround } from './lightrule';

/** What a player may pick. AUTO follows the faction the surface knows. */
export const THEME_CHOICES = ['auto', 1, 2, 3, 4, 5] as const;
export type ThemeChoice = (typeof THEME_CHOICES)[number];

/** The theme AUTO falls back to before an account or a `Hello` arrives.
 *  It is today's look, so nothing flashes on the first paint. */
export const FALLBACK_FACTION = 1;

/** WCAG AA for body text. `--text` on the panel ink must clear it. */
export const TEXT_CONTRAST_FLOOR = 4.5;

/** WCAG 3 : 1, the floor of large text and of a mark. `--faint` on the
 *  panel and on the sheet must clear it in a custom theme (themes.md §6.2,
 *  R2116): faint text names a secondary line, and the floor of body text
 *  would refuse the faction themes themselves. */
export const FAINT_CONTRAST_FLOOR = 3;

/** A colour as three 0..255 channels. */
export type Rgb = [number, number, number];

/**
 * The ink family the chrome was drawn with (ui-map-interface.md §2).
 *
 * These are the values of `[presentation.palette]`, which R1534 lifted a
 * little for dark screens. Every theme is this table with one hue turn
 * applied, so the table is the one copy of the chrome's lightness and
 * chroma.
 */
const BASE: Record<string, Rgb> = {
  text: [163, 207, 230],   // --text  #a3cfe6
  dim: [94, 137, 160],     // --dim   #5e89a0
  faint: [76, 123, 147],   // --faint #4c7b93
  panel: [6, 22, 35],      // --panel-rgb, the panel ink
  sheet: [4, 18, 29],      // --sheet-rgb, a panel that sits on a panel
  ink: [4, 15, 26],        // --ink-rgb, the page ink
  deep: [2, 11, 18],       // --deep-rgb, the deepest page ink
};

/** How far the panel ink covers what is behind it (`--panel` alpha). */
const PANEL_ALPHA = CHROME_PANEL_ALPHA;

/** The step the text lightness is lifted by when a theme misses the
 *  contrast floor. Small enough that the nudge is not a new colour. */
const NUDGE_STEP = CHROME_NUDGE_STEP;

/** `#rrggbb`, the form a custom property takes. */
export function hexOf(c: Rgb): string {
  return '#' + c.map((v) => v.toString(16).padStart(2, '0')).join('');
}

/** `R G B`, the form `rgb(var(--x) / .5)` takes. */
function channelText(c: Rgb): string {
  return c.join(' ');
}

// --- OKLab ------------------------------------------------------------
// The space the whole derivation is done in. Its L is perceived lightness
// and the distance from its origin is perceived chroma, which is what HSL
// gets wrong and what R255 is about.

/** Lightness, and the two opponent axes. */
export type Lab = [number, number, number];

/** Lightness, chroma and hue in degrees. */
export type Lch = [number, number, number];

function srgbToLinear(v: number): number {
  const u = v / 255;
  return u <= 0.04045 ? u / 12.92 : Math.pow((u + 0.055) / 1.055, 2.4);
}

function linearToSrgb(v: number): number {
  const u = v <= 0.0031308 ? v * 12.92 : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
  return u * 255;
}

export function rgbToOklab([r, g, b]: Rgb): Lab {
  const R = srgbToLinear(r), G = srgbToLinear(g), B = srgbToLinear(b);
  const l = Math.cbrt(0.4122214708 * R + 0.5363325363 * G + 0.0514459929 * B);
  const m = Math.cbrt(0.2119034982 * R + 0.6806995451 * G + 0.1073969566 * B);
  const s = Math.cbrt(0.0883024619 * R + 0.2817188376 * G + 0.6299787005 * B);
  return [
    0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s,
  ];
}

/** OKLab back to sRGB, unrounded and unclamped, so `fit` can see a
 *  channel leave the gamut. */
function oklabToRgbRaw([L, a, b]: Lab): [number, number, number] {
  const l = Math.pow(L + 0.3963377774 * a + 0.2158037573 * b, 3);
  const m = Math.pow(L - 0.1055613458 * a - 0.0638541728 * b, 3);
  const s = Math.pow(L - 0.0894841775 * a - 1.2914855480 * b, 3);
  return [
    linearToSrgb(+4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
    linearToSrgb(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
    linearToSrgb(-0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s),
  ];
}

/**
 * OKLab to a colour a browser can take.
 *
 * **One gamut policy, in one place: a colour that does not fit gives up
 * chroma and never lightness.** Lightness is what the layer order of the
 * chrome depends on — a sheet must stay darker than the panel it sits on
 * — and a hue that is a few per cent short of its chroma is a thing
 * nobody sees. Faction 4's turn is the one that needs this today.
 */
export function fit([L, a, b]: Lab): Rgb {
  const k = fitScale([L, a, b]);
  return oklabToRgbRaw([L, a * k, b * k])
    .map((v) => Math.max(0, Math.min(255, Math.round(v)))) as Rgb;
}

/** The share of its chroma a colour keeps under `fit`: 1 in the gamut. */
export function fitScale([L, a, b]: Lab): number {
  let k = 1;
  for (let i = 0; i < 40; i++) {
    const out = oklabToRgbRaw([L, a * k, b * k]);
    if (out.every((v) => v >= -0.5 && v <= 255.5)) break;
    k -= 0.025;
  }
  return k;
}

/** OKLCh to OKLab. */
export function labOf([L, C, H]: Lch): Lab {
  const h = (H * Math.PI) / 180;
  return [L, C * Math.cos(h), C * Math.sin(h)];
}

export function oklch(c: Rgb): Lch {
  const [L, a, b] = rgbToOklab(c);
  return [L, Math.hypot(a, b), ((Math.atan2(b, a) * 180) / Math.PI + 360) % 360];
}

/**
 * sRGB to HSL: hue in degrees, saturation and lightness in per cent. The
 * workshop only SHOWS this (R821). The suggested range and the clamp work
 * in OKLCh, because HSL saturation is not what an eye reads (R255).
 */
export function rgbToHsl([r, g, b]: Rgb): [number, number, number] {
  const R = r / 255, G = g / 255, B = b / 255;
  const max = Math.max(R, G, B), min = Math.min(R, G, B);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return [0, 0, l * 100];
  const s = d / (1 - Math.abs(2 * l - 1));
  let h = max === R ? ((G - B) / d) % 6 : max === G ? (B - R) / d + 2 : (R - G) / d + 4;
  h *= 60;
  if (h < 0) h += 360;
  return [h, s * 100, l * 100];
}

/** Turns one colour's hue by `deg` and holds its lightness and chroma. */
function turnHue(c: Rgb, deg: number): Rgb {
  const [L, C, H] = oklch(c);
  return fit(labOf([L, C, H + deg]));
}

/** Lifts one colour's perceived lightness and holds its hue and chroma. */
function liftLightness(c: Rgb, by: number): Rgb {
  const [L, a, b] = rgbToOklab(c);
  return fit([Math.min(1, L + by), a, b]);
}

/** WCAG relative luminance. */
function luminance([r, g, b]: Rgb): number {
  return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
}

/** WCAG contrast ratio, 1..21. */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = luminance(a), lb = luminance(b);
  const hi = Math.max(la, lb), lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

/** One colour laid over another at `alpha`. */
export function over(top: Rgb, under: Rgb, alpha: number): Rgb {
  return top.map((v, i) =>
    Math.round(v * alpha + under[i] * (1 - alpha))) as Rgb;
}

/**
 * What a panel actually shows behind its text: the panel ink at its own
 * alpha over the deepest page ink. The chrome floats on the map, but the
 * map is dark water and the page ink is the honest worst case a reader
 * gets.
 */
export function panelInk(panel: Rgb, deep: Rgb): Rgb {
  return over(panel, deep, PANEL_ALPHA);
}

/** The faction colour, as channels. An id outside 1..5 takes faction 1.
 *  The page root takes the dark ink, `banner`, in both identities: the
 *  light body of the game chrome is `ui/lightchrome.ts` (R3298). */
function factionRgb(id: number): Rgb {
  return channels((factionOf(id) ?? FACTIONS[0]).banner);
}

/** The angle every token of a theme is turned by. Faction 1 turns by zero. */
export function hueShift(id: number): number {
  const base = oklch(factionRgb(FALLBACK_FACTION))[2];
  return oklch(factionRgb(id))[2] - base;
}

/** One theme's colours, before they become custom properties. */
export interface Palette {
  accent: Rgb;
  text: Rgb;
  dim: Rgb;
  faint: Rgb;
  panel: Rgb;
  sheet: Rgb;
  ink: Rgb;
  deep: Rgb;
  /** How many nudge steps the text needed to clear the contrast floor. */
  nudged: number;
}

/**
 * Derives one faction's palette.
 *
 * The nudge is taken on `--text` and never on `--accent`: the accent has
 * to keep matching the banner on the map, and the text has no such duty.
 * In this build no faction needs it — the turn holds the lightness that
 * already cleared the floor, and R255 holds it more tightly than R243 did
 * — but the floor is enforced here rather than assumed, because a faction
 * colour is data and can change.
 */
export function palette(id: number): Palette {
  const deg = hueShift(id);
  const turn = (k: keyof typeof BASE): Rgb => turnHue(BASE[k], deg);
  const panel = turn('panel');
  const deep = turn('deep');
  const ground = panelInk(panel, deep);
  let text = turn('text');
  let nudged = 0;
  while (contrastRatio(text, ground) < TEXT_CONTRAST_FLOOR && nudged < 100) {
    nudged += 1;
    const next = liftLightness(text, NUDGE_STEP * nudged);
    if (next.every((v, i) => v === text[i])) break;
    text = next;
  }
  return {
    accent: factionRgb(id),
    text,
    dim: turn('dim'),
    faint: turn('faint'),
    panel,
    sheet: turn('sheet'),
    ink: turn('ink'),
    deep,
    nudged,
  };
}

/**
 * The custom properties one theme writes, as name → value.
 *
 * `--panel` and `--edge` are deliberately absent: `styles.css` composes
 * them from `--panel-rgb` and `--accent-rgb`, so their alpha has one home
 * and this file never restates it.
 */
export function themeTokens(id: number): Record<string, string> {
  const p = palette(id);
  return {
    '--accent': hexOf(p.accent),
    '--accent-rgb': channelText(p.accent),
    '--text': hexOf(p.text),
    '--text-rgb': channelText(p.text),
    '--dim': hexOf(p.dim),
    '--faint': hexOf(p.faint),
    '--faint-rgb': channelText(p.faint),
    '--panel-rgb': channelText(p.panel),
    '--sheet-rgb': channelText(p.sheet),
    '--ink-rgb': channelText(p.ink),
    '--deep-rgb': channelText(p.deep),
  };
}

/** Which faction a choice resolves to. AUTO takes the surface's faction,
 *  and falls back to the Abyssal Compact before one is known. */
export function resolveTheme(choice: ThemeChoice, factionId: number): number {
  const id = choice === 'auto' ? factionId : choice;
  return factionOf(id) ? id : FALLBACK_FACTION;
}

/** The theme written last, so a repeat write does no style work. */
let applied = -1;
/** True when the meta and the device store hold the ink of `applied`. */
let inkStored = false;
/** The custom body written last, as JSON, so a repeat does no style work. */
let appliedBody = '';
/** The properties the custom body wrote on the root. */
let bodyNames: string[] = [];

/**
 * Writes one theme onto the document root. This is the single owner of
 * the palette custom properties: nothing else in the client may write one.
 *
 * It is cheap to call on every settings write, because a repeat of the
 * theme already on the root does nothing at all.
 *
 * `store` false wears a theme that is not the player's own look: AUTO
 * before the account states a faction, or the seat team of a match or a
 * replay. Such a write keeps the stored ink of the next first paint
 * (R1953). The next write with `store` stores the ink, also when it
 * resolves to the same theme.
 */
export function applyTheme(choice: ThemeChoice, factionId: number, store = true): void {
  const id = resolveTheme(choice, factionId);
  if (id === applied) {
    if (store && !inkStored) storeInk(id);
    return;
  }
  appliedBody = '';
  inkStored = false;
  if (typeof document === 'undefined') { applied = id; return; }
  const root = document.documentElement;
  // A custom body wrote two alphas and the two composed tokens as well
  // (`applyThemeBody`). A faction theme writes neither, so they go, and the
  // token sheet's own values stand again.
  for (const name of bodyNames) root.style.removeProperty(name);
  bodyNames = [];
  const tokens = themeTokens(id);
  for (const [name, value] of Object.entries(tokens)) {
    root.style.setProperty(name, value);
  }
  root.dataset.theme = String(id);
  applied = id;
  if (store) storeInk(id);
}

/** Stores the page ink of one faction theme (`setThemeColor`). */
function storeInk(id: number): void {
  if (typeof document !== 'undefined') setThemeColor(hexOf(palette(id).ink));
  inkStored = true;
}

/**
 * The device key of the page ink of the theme worn last (R1917). iOS takes
 * the band behind the status bar and the Dynamic Island at the first paint
 * and keeps it, before any theme applies, so `public/entrycover.js` reads
 * this key and paints the head of the page in the stored ink.
 */
export const THEME_INK_KEY = 'ap.ink';

/**
 * The device key of the light page ink of the theme worn last (R3320):
 * the page ink of its light body, which `public/identity.js` reads before
 * the first paint of a page in the light identity.
 */
export const THEME_INK_LIGHT_KEY = 'ap.ink.light';

/** The page ink of the light body of a dark body whose page ink is
 *  `hex`: the ground rule at the ladder lightness of the page ink. */
export function lightPageInk(hex: string): string {
  return chromeGround(LIGHT_CHROME_RULE, LIGHT_CHROME_RULE.ladder.ink, hex);
}

/**
 * Writes the page ink of the worn theme into the `theme-color` meta, into
 * `--boot-ink` on the root and into the device store, so the next first
 * paint starts in this ink. The cover and the page ground read
 * `--boot-ink` before the token ink (R1956). The store keeps the ink of
 * both identities, so a switch of the identity paints the right ink first;
 * the page writes the ink of its own identity (R3320).
 */
function setThemeColor(hex: string): void {
  const light = lightPageInk(hex);
  const ink = LIGHT ? light : hex;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', ink);
  document.documentElement.style.setProperty('--boot-ink', ink);
  writeStore(THEME_INK_KEY, hex);
  writeStore(THEME_INK_LIGHT_KEY, light);
}

// --- the team ink of an arcade match (R2051) -----------------------------
//
// A match wears the neutral chrome: every control stays on the accent of
// the first paint. The seat's team is an identity, so it rides a token of
// its own, `--team`, which only the identity marks read: the banner dot of
// the readout line, the own row of the roster and of the end table. The
// team ink never reaches `--accent`, because the banners of factions 2 and
// 3 are the Amber and the Bad of the verdict inks.

/** The two identity tokens of one team, as name → value. */
export function teamTokens(team: number): Record<string, string> {
  const c = factionRgb(team);
  return { '--team': hexOf(c), '--team-rgb': channelText(c) };
}

/**
 * Writes the team ink onto the document root. A team under 1 (a record's
 * omniscient channel, or a page with no seat) takes both tokens away, and
 * the sheet's `--team` then reads the accent the chrome wears.
 */
export function wearTeamInk(team: number): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement.style;
  const tokens = teamTokens(team);
  for (const [name, value] of Object.entries(tokens)) {
    if (team >= 1) root.setProperty(name, value);
    else root.removeProperty(name);
  }
}

/**
 * Writes the theme of an arcade match or of a record (R2051): the neutral
 * chrome, which is the first paint's own palette, and the seat's team on
 * `--team`. `team` under 1 is no seat (the omniscient channel of a record),
 * and the identity marks then read the accent. The team is not the
 * player's own look, so the stored ink of the next first paint stays
 * (R1953).
 */
export function wearSeatTeam(team: number): void {
  applyTheme(FALLBACK_FACTION, FALLBACK_FACTION, false);
  wearTeamInk(team);
}

/** Forgets what was written last. Tests only. */
export function resetAppliedTheme(): void {
  applied = -1; appliedBody = ''; bodyNames = []; inkStored = false;
}

/**
 * Writes one custom UI theme body onto the document root (themes.md §3.1,
 * R814). It is the page-wide twin of `applyThemeTo` below, and it writes
 * the same tokens: the chrome's channel tokens and the two alphas, never a
 * faction colour, a resource hue, `--bad`, `--good` or `--gold`. The next
 * `applyTheme` takes the extra properties away again.
 */
export function applyThemeBody(body: ThemeBody): void {
  const json = JSON.stringify(body);
  if (json === appliedBody) return;
  appliedBody = json;
  applied = -2;
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const tokens = bodyTokens(body);
  for (const [name, value] of Object.entries(tokens)) root.style.setProperty(name, value);
  bodyNames = Object.keys(tokens);
  root.dataset.theme = 'custom';
  setThemeColor(body.ink);
  inkStored = true;
}

// --- one theme on one element (R806) -----------------------------------
//
// `applyTheme` above writes the page's theme onto the document root, and it
// stays exactly as it was. The theme preview must show a theme inside its
// own frame without changing the page, so these three functions write a
// theme BODY onto one element. The body is the UI theme's shape: the seven
// channel tokens of ui-map-interface.md §2.1 and the two alphas.

/**
 * One UI theme as a body states it (R806): the seven channel tokens as
 * `#rrggbb` and the two alphas of the token sheet. `dim` is the one token of
 * the chrome that carries no channels; a body without it takes the dim ink
 * `derivedDim` mixes from its own text and faint (R1776).
 */
export interface ThemeBody {
  accent: string;
  text: string;
  faint: string;
  panel: string;
  sheet: string;
  ink: string;
  deep: string;
  dim?: string;
  panelAlpha: number;
  edgeAlpha: number;
}

/** One faction's theme as a body. It carries the values `themeTokens` writes. */
export function themeBody(id: number): ThemeBody {
  const p = palette(id);
  return {
    accent: hexOf(p.accent),
    text: hexOf(p.text),
    faint: hexOf(p.faint),
    panel: hexOf(p.panel),
    sheet: hexOf(p.sheet),
    ink: hexOf(p.ink),
    deep: hexOf(p.deep),
    dim: hexOf(p.dim),
    panelAlpha: CHROME_PANEL_ALPHA,
    edgeAlpha: CHROME_EDGE_ALPHA,
  };
}

/** Where the base `dim` stands between `faint` (0) and `text` (1), in
 *  OKLab lightness. */
const DIM_AT = (() => {
  const l = (c: Rgb): number => rgbToOklab(c)[0];
  return (l(BASE.dim) - l(BASE.faint)) / (l(BASE.text) - l(BASE.faint));
})();

/**
 * The dim ink of a body that states none (R1776): the OKLab mix of its own
 * `faint` and `text` at the place the base palette puts `dim`. A UI body
 * holds no `dim` (themes.md §3.1), and without this the root kept the dim
 * ink of the theme worn before.
 */
export function derivedDim(text: string, faint: string): string {
  const a = rgbToOklab(rgbOfHex(faint));
  const b = rgbToOklab(rgbOfHex(text));
  return hexOf(fit([0, 1, 2].map((i) => a[i] + (b[i] - a[i]) * DIM_AT) as Lab));
}

/** The channels of a `#rrggbb` string; a string that is no number is black. */
export function rgbOfHex(hex: string): Rgb {
  return channels(packHex(hex));
}

/**
 * The custom properties one body writes onto an element, as name → value.
 *
 * `--panel` and `--edge` are declared again here, and that is not a second
 * home for their alphas. A custom property that reads another one resolves
 * on the element that declares it, so the two that the token sheet composes
 * on `:root` hold the page's channels on every descendant. The element must
 * compose them again from its own channels. `theme.body.test.ts` pins the
 * two formulas to the token sheet.
 */
export function bodyTokens(b: ThemeBody): Record<string, string> {
  const rgb = (hex: string): string => channelText(rgbOfHex(hex));
  return {
    '--accent': b.accent,
    '--accent-rgb': rgb(b.accent),
    '--text': b.text,
    '--text-rgb': rgb(b.text),
    '--dim': b.dim ?? derivedDim(b.text, b.faint),
    '--faint': b.faint,
    '--faint-rgb': rgb(b.faint),
    '--panel-rgb': rgb(b.panel),
    '--sheet-rgb': rgb(b.sheet),
    '--ink-rgb': rgb(b.ink),
    '--deep-rgb': rgb(b.deep),
    '--panel-alpha': String(b.panelAlpha),
    '--edge-alpha': String(b.edgeAlpha),
    '--panel': 'rgb(var(--panel-rgb) / var(--panel-alpha))',
    '--edge': 'rgb(var(--accent-rgb) / var(--edge-alpha))',
  };
}

/**
 * Writes one theme body onto one element and its subtree (R806). The page
 * and its root are not touched, so a preview frame shows a theme the page
 * does not wear. Every call writes every property; it holds no memory.
 */
export function applyThemeTo(el: HTMLElement, body: ThemeBody): void {
  for (const [name, value] of Object.entries(bodyTokens(body))) {
    el.style.setProperty(name, value);
  }
}
